import { z } from 'zod';

/**
 * Erlaubte Entity-Typen für das polymorphe Kommentar-System.
 */
export const CommentEntityTypeSchema = z.enum([
  'user',
  'ticket',
  'registration',
  'feature_request',
]);

/** TypeScript Type für Kommentar-Entity-Typ */
export type CommentEntityType = z.infer<typeof CommentEntityTypeSchema>;

/**
 * Schema für die Erstellung eines Kommentars.
 * Wird für POST /comments genutzt.
 *
 * @example
 * ```ts
 * const result = CreateCommentSchema.safeParse(body);
 * ```
 */
export const CreateCommentSchema = z.object({
  /** Typ der verknüpften Entität */
  entityType: CommentEntityTypeSchema,
  /** ID der verknüpften Entität (kein FK — polymorphe Relation) */
  entityId: z.string().min(1),
  /** Kommentar-Text */
  text: z.string().min(1).max(5000),
});

/** TypeScript Type für Kommentar-Erstellung */
export type CreateComment = z.infer<typeof CreateCommentSchema>;

/**
 * Basis-Schema für einen Kommentar.
 */
export const CommentSchema = z.object({
  id: z.string(),
  entityType: CommentEntityTypeSchema,
  entityId: z.string(),
  userId: z.string(),
  text: z.string(),
  createdAt: z.coerce.date(),
});

/** TypeScript Type für Kommentar */
export type Comment = z.infer<typeof CommentSchema>;

/**
 * Kommentar mit Autor-Daten für die Timeline-Darstellung im Dashboard.
 */
export const CommentWithAuthorSchema = CommentSchema.extend({
  author: z.object({
    id: z.string(),
    firstName: z.string().nullable().optional(),
    lastName: z.string().nullable().optional(),
    displayName: z.string().nullable().optional(),
    avatarUrl: z.string().nullable().optional(),
    email: z.string().email(),
  }),
});

/** TypeScript Type für Kommentar mit Autor-Daten */
export type CommentWithAuthor = z.infer<typeof CommentWithAuthorSchema>;

/**
 * Response-Schema für GET /comments.
 * Liste von Kommentaren mit Autor-Daten.
 *
 * @example
 * ```ts
 * const result = CommentsListResponseSchema.parse(response);
 * ```
 */
export const CommentsListResponseSchema = z.array(CommentWithAuthorSchema);

/** TypeScript Type für die Kommentar-Liste-Response */
export type CommentsListResponse = z.infer<typeof CommentsListResponseSchema>;
